import * as React from 'react';
import {addDays, addHours, addMinutes, format, parse} from 'date-fns';
import {AirbnbSelect, type AirbnbSelectProps} from '../select';
import {type SelectOption} from '../../lib/selector-option';
import type {TimePickerFormat, TimeSettings} from '../../fields/time-picker';

type AirbnbTimePickerProps = Omit<
  AirbnbSelectProps<unknown, string, string>,
  'options'
> & {
  format?: TimePickerFormat;
  timeSettings?: TimeSettings;
  options?: AirbnbSelectProps<unknown, string, string>['options'];
};

const SHORT_TIME_FORMAT = 'HH:mm';

function parseTime(value: string) {
  return parse(value, SHORT_TIME_FORMAT, new Date());
}

function getRange(
  settings: Required<Omit<TimeSettings, 'addNextDay'>> & {
    addNextDay: boolean;
  },
) {
  const min = parseTime(settings.min_time);
  const max =
    settings.max_time === '00:00' && settings.addNextDay
      ? addDays(parseTime(settings.max_time), 1)
      : parseTime(settings.max_time);
  return {min, max};
}

function buildOptions(settings: TimeSettings): SelectOption<unknown, string, string>[] {
  const interval_unit = settings.interval_unit ?? 'M';
  const interval = settings.interval ?? (interval_unit === 'H' ? 1 : 60);
  const min_time = settings.min_time ?? '00:00';
  const max_time = settings.max_time ?? (interval_unit === 'H' ? '23:00' : '23:00');
  const addNextDay = settings.addNextDay !== false;
  const {min, max} = getRange({interval_unit, interval, min_time, max_time, addNextDay});

  const options: SelectOption<unknown, string, string>[] = [];
  let current = new Date(min.getTime());
  while (current.getTime() <= max.getTime()) {
    const text = format(current, SHORT_TIME_FORMAT);
    options.push({value: text, label: text});
    current =
      interval_unit === 'H' ? addHours(current, interval) : addMinutes(current, interval);
  }
  return options;
}

const FORMAT_SETTINGS: Record<TimePickerFormat, TimeSettings> = {
  time: {interval_unit: 'M', interval: 30, min_time: '00:00', max_time: '23:30'},
  timeEach15Minutes: {
    interval_unit: 'M',
    interval: 15,
    min_time: '00:00',
    max_time: '23:45',
  },
  timeEach30Minutes: {
    interval_unit: 'M',
    interval: 30,
    min_time: '00:00',
    max_time: '23:30',
  },
  hours: {interval_unit: 'H', interval: 1, min_time: '00:00', max_time: '23:00'},
};

const AirbnbTimePicker = React.forwardRef<HTMLButtonElement, AirbnbTimePickerProps>(
  function AirbnbTimePicker(
    {format: formatName = 'time', timeSettings, options, ...selectProps},
    ref,
  ) {
    const resolvedOptions = React.useMemo(() => {
      if (options) return options;
      const settings = timeSettings ?? FORMAT_SETTINGS[formatName];
      return buildOptions(settings);
    }, [formatName, options, timeSettings]);

    return <AirbnbSelect ref={ref} {...selectProps} options={resolvedOptions} />;
  },
);

export {AirbnbTimePicker};
export type {AirbnbTimePickerProps};
